import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Reveal } from '../Reveal';
import { SectionEyebrow } from '../SectionEyebrow';
const steps = [{
  need: 'Si necesitas claridad,',
  then: 'vuelve al pensamiento estratégico.'
}, {
  need: 'Si necesitas fuerza,',
  then: 'vuelve a la disciplina.'
}, {
  need: 'Si necesitas calma,',
  then: 'vuelve a tus emociones.'
}, {
  need: 'Si necesitas dirección,',
  then: 'vuelve a tu vocación.'
}, {
  need: 'Si necesitas reconectar,',
  then: 'vuelve a tu esencia.'
}];
const TimelineNode: React.FC<{
  index: number;
  total: number;
  need: string;
  then: string;
}> = ({
  index,
  need,
  then
}) => {
  const isEven = index % 2 === 0;
  return <motion.div initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.8,
    delay: index * 0.1,
    ease: [0.22, 1, 0.36, 1]
  }} className="relative grid grid-cols-[1fr_40px_1fr] sm:grid-cols-[1fr_56px_1fr] items-center gap-0" data-id="element-230">
      {/* Left content */}
      <div className={`hidden sm:block pr-4 sm:pr-8 ${isEven ? '' : 'sm:invisible'}`} data-id="element-231">
        {isEven && <motion.p className="text-ivory/80 leading-relaxed text-right" initial={{
        opacity: 0,
        x: -16
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        margin: '-40px'
      }} transition={{
        duration: 0.6,
        delay: index * 0.1 + 0.15
      }} data-id="element-232">
            <span className="font-serif italic text-gold" data-id="element-233">{need}</span>{' '}
            <span className="font-light" data-id="element-234">{then}</span>
          </motion.p>}
      </div>

      {/* Center node */}
      <div className="flex justify-center" data-id="element-235">
        <motion.div className="relative z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center" initial={{
        scale: 0,
        opacity: 0
      }} whileInView={{
        scale: 1,
        opacity: 1
      }} viewport={{
        once: true,
        margin: '-40px'
      }} transition={{
        duration: 0.5,
        delay: index * 0.1 + 0.1,
        ease: [0.22, 1, 0.36, 1]
      }} style={{
        background: 'radial-gradient(circle, rgba(212,175,122,0.3), rgba(14,26,58,1))',
        border: '1px solid rgba(212,175,122,0.5)',
        boxShadow: '0 0 24px rgba(212,175,122,0.4)'
      }} data-id="element-236">
          <span className="font-serif italic text-gold text-sm" data-id="element-237">
            {index + 1}
          </span>

          {/* Pulse ring */}
          <motion.div className="absolute inset-0 rounded-full border border-gold/40" initial={{
          scale: 1,
          opacity: 0.7
        }} whileInView={{
          scale: 2,
          opacity: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 1,
          delay: index * 0.1 + 0.2,
          ease: 'easeOut'
        }} data-id="element-238" />
        </motion.div>
      </div>

      {/* Right content */}
      <div className={`hidden sm:block pl-4 sm:pl-8 ${isEven ? 'sm:invisible' : ''}`} data-id="element-239">
        {!isEven && <motion.p className="text-ivory/80 leading-relaxed text-left" initial={{
        opacity: 0,
        x: 16
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        margin: '-40px'
      }} transition={{
        duration: 0.6,
        delay: index * 0.1 + 0.15
      }} data-id="element-240">
            <span className="font-serif italic text-gold" data-id="element-241">{need}</span>{' '}
            <span className="font-light" data-id="element-242">{then}</span>
          </motion.p>}
      </div>

      {/* Mobile: show content on right always */}
      <div className="col-span-3 sm:hidden mt-3 pl-14" data-id="element-243">
        <motion.p className="text-ivory/80 leading-relaxed text-left" initial={{
        opacity: 0,
        x: 10
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        margin: '-40px'
      }} transition={{
        duration: 0.6,
        delay: index * 0.1 + 0.15
      }} data-id="element-244">
          <span className="font-serif italic text-gold" data-id="element-245">{need}</span>{' '}
          <span className="font-light" data-id="element-246">{then}</span>
        </motion.p>
      </div>
    </motion.div>;
};
export const Journey: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start 0.8', 'end 0.6']
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  const lineOpacity = useTransform(scrollYProgress, [0, 0.05, 1], [0, 1, 1]);
  return <section className="relative bg-navy-mid py-28 lg:py-44 overflow-hidden" data-id="element-247">
      <div className="absolute inset-0" style={{
      background: 'radial-gradient(ellipse at 50% 30%, rgba(168,197,224,0.1), transparent 60%)'
    }} data-id="element-248" />

      <div className="relative max-w-3xl mx-auto px-6 lg:px-8" data-id="element-249">
        <div className="text-center mb-16" data-id="element-250">
          <Reveal data-id="element-251">
            <SectionEyebrow data-id="element-252">El ritmo del libro</SectionEyebrow>
          </Reveal>
          <Reveal delay={0.1} data-id="element-253">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ivory mb-6 leading-[1.1]" style={{
            fontWeight: 300
          }} data-id="element-254">
              Una lectura para{' '}
              <span className="italic gold-text" style={{
              fontWeight: 400
            }} data-id="element-255">
                volver a tu centro
              </span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} data-id="element-256">
            <p className="text-ivory/65 text-lg font-light leading-[1.8]" data-id="element-257">
              Este libro está diseñado para acompañarte con calma. Puedes leerlo
              de principio a fin, o regresar a un capítulo específico cuando
              sientas que una dimensión de tu vida necesita atención.
            </p>
          </Reveal>
        </div>

        <div className="relative" ref={containerRef} data-id="element-258">
          {/* Background track line */}
          <div className="absolute left-5 sm:left-1/2 sm:-translate-x-1/2 top-0 bottom-0 w-px bg-gold/10 rounded-full" data-id="element-259" />

          {/* Animated fill line */}
          <div className="absolute left-5 sm:left-1/2 sm:-translate-x-1/2 top-0 w-[2px] overflow-hidden rounded-full" style={{
          height: '100%'
        }} data-id="element-260">
            <motion.div className="w-full origin-top" style={{
            height: lineHeight,
            opacity: lineOpacity,
            background: 'linear-gradient(180deg, rgba(212,175,122,0.8), rgba(212,175,122,0.3) 85%, transparent)',
            boxShadow: '0 0 12px rgba(212,175,122,0.5)'
          }} data-id="element-261" />
          </div>

          <div className="space-y-12 sm:space-y-14" data-id="element-262">
            {steps.map((s, i) => <TimelineNode key={i} index={i} total={steps.length} need={s.need} then={s.then} data-id="element-263" />)}
          </div>

          {/* End glow dot */}
          <motion.div className="absolute left-[14px] sm:left-1/2 sm:-translate-x-1/2 -bottom-4 w-3 h-3 rounded-full" initial={{
          scale: 0,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: steps.length * 0.1 + 0.4,
          duration: 0.6
        }} style={{
          background: '#D4AF7A',
          boxShadow: '0 0 20px rgba(212,175,122,0.9), 0 0 40px rgba(212,175,122,0.4)'
        }} data-id="element-264" />
        </div>
      </div>
    </section>;
};